///home/ec2-user/brand-insights-dashboard/frontend/src/components/Charts.js
// Charts.js - Componenti grafici interattivi con filtro cross-dimensional

import React, { useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  Box, Paper, Typography, useTheme, CircularProgress,
  Chip, Button
} from '@mui/material';
import { FilterAlt, Close } from '@mui/icons-material';

// ============================================
// 1. DIMENSION CHART INTERATTIVO - OTTIMIZZATO
// ============================================

export const DimensionChart = React.memo(({ 
  dimension, 
  kpi, 
  data = [], 
  title,
  height = 300,
  maxItems = 15,
  isLoading = false,
  onBarClick,
  activeFilter = null
}) => {
  const theme = useTheme();
  
  // ✅ OTTIMIZZAZIONE: useMemo per chartData
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data
      .filter(item => item.dimension_value && item.dimension_value.trim() !== '')
      .map(item => ({
        name: item.dimension_value,
        value: item[kpi] || 0,
        rawData: item
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, maxItems);
  }, [data, kpi, maxItems]);
  
  // ✅ OTTIMIZZAZIONE: useMemo per totalValue (PRIMA dei return condizionali!)
  const totalValue = useMemo(() => 
    chartData.reduce((sum, item) => sum + item.value, 0), 
    [chartData]
  );
  
  // ✅ OTTIMIZZAZIONE: useCallback per formattazione valore
  const formatValue = useCallback((value) => {
    if (kpi === 'engagement_rate') {
      return `${value.toFixed(2)}%`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  }, [kpi]);
  
  // ✅ OTTIMIZZAZIONE: useMemo per colore
  const getColor = useCallback((index, valueName) => {
    const isActive = activeFilter && 
      activeFilter.dimension === dimension && 
      activeFilter.value === valueName;
    
    if (isActive) {
      return theme.palette.error.main;
    }
    
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
    ];
    return colors[index % colors.length];
  }, [activeFilter, dimension, theme]);
  
  // ✅ OTTIMIZZAZIONE: useCallback per handleBarClick
  const handleBarClick = useCallback((data, index) => {
    if (onBarClick && data && data.name) {
      onBarClick({
        dimension: dimension,
        value: data.name,
        label: `${title}: ${data.name}`
      });
    }
  }, [onBarClick, dimension, title]);
  
  if (isLoading) {
    return (
      <Paper sx={{ p: 2, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }
  
  if (!chartData || chartData.length === 0) {
    return (
      <Paper sx={{ p: 2, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Nessun dato disponibile
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Paper sx={{ 
      p: 2, 
      height, 
      display: 'flex', 
      flexDirection: 'column',
      border: activeFilter && activeFilter.dimension === dimension ? 
        `2px solid ${theme.palette.error.main}` : '1px solid',
      borderColor: activeFilter && activeFilter.dimension === dimension ? 
        theme.palette.error.main : 'divider'
    }}>
      {/* Header con titolo - RIMOSSO il Chip "Clicca per filtrare" */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1" noWrap title={title}>
          {title}
        </Typography>
      </Box>
      
      {/* Grafico */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis 
              type="number" 
              tickFormatter={formatValue}
            />
            <YAxis 
              type="category" 
              dataKey="name"
              width={100}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => 
                value.length > 15 ? `${value.substring(0, 15)}...` : value
              }
            />
            <Tooltip
              formatter={(value) => [formatValue(value), kpi.replace('_', ' ')]}
              labelFormatter={(label) => `Valore: ${label}`}
            />
            <Bar 
              dataKey="value" 
              radius={[0, 4, 4, 0]}
              onClick={handleBarClick}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${entry.name || index}`} 
                  fill={getColor(index, entry.name)}
                  style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      
      {/* RIMOSSO: Footer con info (valori e totale) */}
    </Paper>
  );
}, 
// ✅ OTTIMIZZAZIONE: Funzione di comparazione per React.memo
(prevProps, nextProps) => {
  // Confronta solo le props che influenzano il rendering
  return (
    prevProps.dimension === nextProps.dimension &&
    prevProps.kpi === nextProps.kpi &&
    prevProps.title === nextProps.title &&
    prevProps.height === nextProps.height &&
    prevProps.isLoading === nextProps.isLoading &&
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
    JSON.stringify(prevProps.activeFilter) === JSON.stringify(nextProps.activeFilter)
  );
});

// ============================================
// 2. NUOVI COMPONENTI PER LE 3 SEZIONI INSIGHTS
// ============================================

// ========== AI IMAGE ANALYSIS SECTION ==========
export const AIImageAnalysisSection = React.memo(({ 
  dimensionsData = {},
  selectedKpi = 'total_interactions',
  isLoading = false,
  onDimensionFilter,
  activeDimensionFilter = null
}) => {
  const theme = useTheme();
  
  // ✅ Dimensioni per Image Analysis (6 grafici)
  const imageDimensions = useMemo(() => [
    { value: 'visual_style', label: 'Stile Visuale' },
    { value: 'setting', label: 'Ambientazione' },
    { value: 'primary_value', label: 'Valore Primario' },
    { value: 'content_type', label: 'Tipo di Contenuto' },
    { value: 'target_implied', label: 'Target Implicito' },
    { value: 'cta_type', label: 'Tipo CTA' }
  ], []);
  
  // ✅ OTTIMIZZAZIONE: useCallback per handler
  const handleBarClick = useCallback((filterInfo) => {
    if (onDimensionFilter) {
      // Toggle del filtro
      if (activeDimensionFilter && 
          activeDimensionFilter.dimension === filterInfo.dimension && 
          activeDimensionFilter.value === filterInfo.value) {
        onDimensionFilter(null);
      } else {
        onDimensionFilter(filterInfo);
      }
    }
  }, [onDimensionFilter, activeDimensionFilter]);
  
  // ✅ OTTIMIZZAZIONE: useMemo per il rendering dei grafici
  const dimensionCharts = useMemo(() => 
    imageDimensions.map(dimension => {
      const dataForDimension = dimensionsData[dimension.value] || [];
      
      return (
        <DimensionChart
          key={dimension.value}
          dimension={dimension.value}
          kpi={selectedKpi}
          data={dataForDimension}
          title={dimension.label}
          height={300}
          isLoading={isLoading}
          onBarClick={handleBarClick}
          activeFilter={activeDimensionFilter}
        />
      );
    }), 
    [imageDimensions, dimensionsData, selectedKpi, isLoading, handleBarClick, activeDimensionFilter]
  );
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
        {[...Array(6)].map((_, i) => (
          <Paper key={i} sx={{ p: 2, height: 300 }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={40} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { 
        xs: '1fr', 
        sm: '1fr 1fr', 
        md: '1fr 1fr 1fr' 
      }, 
      gap: 3 
    }}>
      {dimensionCharts}
    </Box>
  );
}, 
// ✅ OTTIMIZZAZIONE: Funzione di comparazione per React.memo
(prevProps, nextProps) => {
  return (
    prevProps.selectedKpi === nextProps.selectedKpi &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onDimensionFilter === nextProps.onDimensionFilter &&
    JSON.stringify(prevProps.dimensionsData) === JSON.stringify(nextProps.dimensionsData) &&
    JSON.stringify(prevProps.activeDimensionFilter) === JSON.stringify(nextProps.activeDimensionFilter)
  );
});

// ========== AI TEXT ANALYSIS SECTION ==========
export const AITextAnalysisSection = React.memo(({ 
  dimensionsData = {},
  selectedKpi = 'total_interactions',
  isLoading = false,
  onDimensionFilter,
  activeDimensionFilter = null
}) => {
  const theme = useTheme();
  
  // ✅ Dimensioni per Text Analysis (3 grafici)
  const textDimensions = useMemo(() => [
    { value: 'sentiment', label: 'Sentiment' },
    { value: 'intent', label: 'Intent' },
    { value: 'category', label: 'Categoria' }
  ], []);
  
  // ✅ OTTIMIZZAZIONE: useCallback per handler
  const handleBarClick = useCallback((filterInfo) => {
    if (onDimensionFilter) {
      // Toggle del filtro
      if (activeDimensionFilter && 
          activeDimensionFilter.dimension === filterInfo.dimension && 
          activeDimensionFilter.value === filterInfo.value) {
        onDimensionFilter(null);
      } else {
        onDimensionFilter(filterInfo);
      }
    }
  }, [onDimensionFilter, activeDimensionFilter]);
  
  // ✅ OTTIMIZZAZIONE: useMemo per il rendering dei grafici
  const dimensionCharts = useMemo(() => 
    textDimensions.map(dimension => {
      const dataForDimension = dimensionsData[dimension.value] || [];
      
      return (
        <DimensionChart
          key={dimension.value}
          dimension={dimension.value}
          kpi={selectedKpi}
          data={dataForDimension}
          title={dimension.label}
          height={300}
          isLoading={isLoading}
          onBarClick={handleBarClick}
          activeFilter={activeDimensionFilter}
        />
      );
    }), 
    [textDimensions, dimensionsData, selectedKpi, isLoading, handleBarClick, activeDimensionFilter]
  );
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Paper key={i} sx={{ p: 2, height: 300 }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={40} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { 
        xs: '1fr', 
        sm: '1fr 1fr', 
        md: '1fr 1fr 1fr' 
      }, 
      gap: 3 
    }}>
      {dimensionCharts}
    </Box>
  );
}, 
// ✅ OTTIMIZZAZIONE: Funzione di comparazione per React.memo
(prevProps, nextProps) => {
  return (
    prevProps.selectedKpi === nextProps.selectedKpi &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onDimensionFilter === nextProps.onDimensionFilter &&
    JSON.stringify(prevProps.dimensionsData) === JSON.stringify(nextProps.dimensionsData) &&
    JSON.stringify(prevProps.activeDimensionFilter) === JSON.stringify(nextProps.activeDimensionFilter)
  );
});

// ========== TIME TO POST SECTION ==========
export const TimeToPostSection = React.memo(({ 
  dimensionsData = {},
  selectedKpi = 'total_interactions',
  isLoading = false,
  onDimensionFilter,
  activeDimensionFilter = null
}) => {
  const theme = useTheme();
  
  // ✅ Dimensioni per Time to Post (3 grafici)
  const timeDimensions = useMemo(() => [
    { value: 'week_day_post', label: 'Giorno della Settimana' },
    { value: 'time_day_post', label: 'Fascia Oraria' },
    { value: 'content_freshness', label: 'Freschezza Contenuto' }
  ], []);
  
  // ✅ OTTIMIZZAZIONE: useCallback per handler
  const handleBarClick = useCallback((filterInfo) => {
    if (onDimensionFilter) {
      // Toggle del filtro
      if (activeDimensionFilter && 
          activeDimensionFilter.dimension === filterInfo.dimension && 
          activeDimensionFilter.value === filterInfo.value) {
        onDimensionFilter(null);
      } else {
        onDimensionFilter(filterInfo);
      }
    }
  }, [onDimensionFilter, activeDimensionFilter]);
  
  // ✅ OTTIMIZZAZIONE: useMemo per il rendering dei grafici
  const dimensionCharts = useMemo(() => 
    timeDimensions.map(dimension => {
      const dataForDimension = dimensionsData[dimension.value] || [];
      
      return (
        <DimensionChart
          key={dimension.value}
          dimension={dimension.value}
          kpi={selectedKpi}
          data={dataForDimension}
          title={dimension.label}
          height={300}
          isLoading={isLoading}
          onBarClick={handleBarClick}
          activeFilter={activeDimensionFilter}
        />
      );
    }), 
    [timeDimensions, dimensionsData, selectedKpi, isLoading, handleBarClick, activeDimensionFilter]
  );
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Paper key={i} sx={{ p: 2, height: 300 }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={40} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { 
        xs: '1fr', 
        sm: '1fr 1fr', 
        md: '1fr 1fr 1fr' 
      }, 
      gap: 3 
    }}>
      {dimensionCharts}
    </Box>
  );
}, 
// ✅ OTTIMIZZAZIONE: Funzione di comparazione per React.memo
(prevProps, nextProps) => {
  return (
    prevProps.selectedKpi === nextProps.selectedKpi &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onDimensionFilter === nextProps.onDimensionFilter &&
    JSON.stringify(prevProps.dimensionsData) === JSON.stringify(nextProps.dimensionsData) &&
    JSON.stringify(prevProps.activeDimensionFilter) === JSON.stringify(nextProps.activeDimensionFilter)
  );
});

// ============================================
// 3. INSIGHTS DASHBOARD INTERATTIVO - OTTIMIZZATO
// (Mantenuto per compatibilità)
// ============================================

export const InsightsDashboard = React.memo(({ 
  dimensionsData = {},
  selectedKpi = 'total_interactions',
  isLoading = false,
  filters = {},
  onDimensionFilter,
  activeDimensionFilter = null
}) => {
  const theme = useTheme();
  
  // ✅ OTTIMIZZAZIONE: useMemo per array costanti
  const insightsDimensions = useMemo(() => [
    { value: 'category', label: 'Categoria' },
    { value: 'content_type', label: 'Tipo di Contenuto' },
    { value: 'primary_value', label: 'Valore Primario' },
    { value: 'visual_style', label: 'Stile Visuale' },
    { value: 'setting', label: 'Ambientazione' },
    { value: 'target_implied', label: 'Target Implicito' },
    { value: 'cta_type', label: 'Tipo CTA' },
    { value: 'sentiment', label: 'Sentiment' },
    { value: 'intent', label: 'Intent' },
    { value: 'week_day_post', label: 'Giorno della Settimana' },
    { value: 'time_day_post', label: 'Fascia Oraria' },
    { value: 'content_freshness', label: 'Freschezza Contenuto' }
  ], []);
  
  // ✅ OTTIMIZZAZIONE: useCallback per handler
  const handleBarClick = useCallback((filterInfo) => {
    if (onDimensionFilter) {
      // Toggle del filtro
      if (activeDimensionFilter && 
          activeDimensionFilter.dimension === filterInfo.dimension && 
          activeDimensionFilter.value === filterInfo.value) {
        onDimensionFilter(null);
      } else {
        onDimensionFilter(filterInfo);
      }
    }
  }, [onDimensionFilter, activeDimensionFilter]);
  
  // ✅ OTTIMIZZAZIONE: useCallback per filtro dati
  const getFilteredData = useCallback((dimension, data) => {
    if (!activeDimensionFilter || !data) return data;
    
    if (activeDimensionFilter.dimension !== dimension) {
      return data;
    }
    
    return data.filter(item => 
      item.dimension_value === activeDimensionFilter.value
    );
  }, [activeDimensionFilter]);
  
  // ✅ OTTIMIZZAZIONE: useMemo per il rendering dei grafici
  const dimensionCharts = useMemo(() => 
    insightsDimensions.map(dimension => {
      const dataForDimension = dimensionsData[dimension.value] || [];
      const filteredData = getFilteredData(dimension.value, dataForDimension);
      
      return (
        <DimensionChart
          key={dimension.value}
          dimension={dimension.value}
          kpi={selectedKpi}
          data={filteredData}
          title={dimension.label}
          height={320}
          isLoading={isLoading}
          onBarClick={handleBarClick}
          activeFilter={activeDimensionFilter}
        />
      );
    }), 
    [insightsDimensions, dimensionsData, selectedKpi, isLoading, handleBarClick, activeDimensionFilter, getFilteredData]
  );
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
        {[...Array(6)].map((_, i) => (
          <Paper key={i} sx={{ p: 2, height: 300 }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={40} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header con filtri attivi - RIMOSSO il box "Analizzando: Total Engagement" */}
      {activeDimensionFilter && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterAlt fontSize="small" color="error" />
              <Chip
                label={`${activeDimensionFilter.label}`}
                color="error"
                size="small"
                onDelete={onDimensionFilter ? () => onDimensionFilter(null) : undefined}
                deleteIcon={<Close />}
              />
              <Typography variant="body2" color="text.secondary">
                Filtro attivo
              </Typography>
            </Box>
            
            {onDimensionFilter && (
              <Button
                size="small"
                startIcon={<Close />}
                onClick={() => onDimensionFilter(null)}
                sx={{ fontSize: '0.75rem' }}
              >
                Rimuovi filtro
              </Button>
            )}
          </Box>
        </Paper>
      )}
      
      {/* Grid di grafici interattivi */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: '1fr 1fr', 
          md: '1fr 1fr 1fr' 
        }, 
        gap: 3 
      }}>
        {dimensionCharts}
      </Box>
      
      {/* Footer info */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
        💡 <strong>Suggerimento:</strong> Clicca su qualsiasi barra per filtrare tutti i grafici per quel valore.
        {activeDimensionFilter && ' Clicca di nuovo sulla stessa barra per rimuovere il filtro.'}
      </Typography>
    </Box>
  );
}, 
// ✅ OTTIMIZZAZIONE: Funzione di comparazione per React.memo
(prevProps, nextProps) => {
  return (
    prevProps.selectedKpi === nextProps.selectedKpi &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onDimensionFilter === nextProps.onDimensionFilter &&
    JSON.stringify(prevProps.dimensionsData) === JSON.stringify(nextProps.dimensionsData) &&
    JSON.stringify(prevProps.activeDimensionFilter) === JSON.stringify(nextProps.activeDimensionFilter)
  );
});

// ============================================
// 4. LOADING SKELETON - OTTIMIZZATO
// ============================================

export const ChartsLoadingSkeleton = React.memo(({ count = 6 }) => {
  const skeletons = useMemo(() => 
    [...Array(count)].map((_, i) => (
      <Paper key={i} sx={{ p: 2, height: 300 }}>
        <Box sx={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          gap: 2 
        }}>
          <Box sx={{ width: '60%', height: 20, bgcolor: 'grey.300', borderRadius: 1 }} />
          <Box sx={{ flex: 1, bgcolor: 'grey.200', borderRadius: 1 }} />
          <Box sx={{ width: '40%', height: 15, bgcolor: 'grey.300', borderRadius: 1 }} />
        </Box>
      </Paper>
    )), 
    [count]
  );
  
  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
      gap: 3 
    }}>
      {skeletons}
    </Box>
  );
});

// ============================================
// 5. EMPTY STATE - OTTIMIZZATO
// ============================================

export const ChartsEmptyState = React.memo(({ message = "Nessun dato disponibile per i filtri selezionati" }) => {
  return (
    <Paper sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        📊 Nessun Dato
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Paper>
  );
});